import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Pagination } from "antd";
import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

import Logo from "../../assets/logo.png";

import InfoCard from "../../components/Common/InfoCard";
import TableHeader from "../../components/Common/TableHeader";
import LandLordTable from "../../components/Table/LandLordTable";
import Loader from "../../components/Common/Loader";
import NotFound from "../../components/NotFound";

import { getTenantPayments } from "../../store/thunks/paymentThunks";
import { useThunk } from "../../hooks/useThunk";
import { formatDateToISO, getDateRangeFromSelection } from "../../utils/dateFormating";
import { svgToCanvas } from "../../utils/svgToCanvas";
import showToast from "../../utils/toastNotifications";

import { monthArray } from "../../constants/commonConstants";

const TenantPaymentActivity = () => {
    const [FetachingGetTenantPayments, isFetchingGetTenantPayments, errorFetachingGetTenantPayments] = useThunk(getTenantPayments);
    
    const [tenantPayments, setTenantPayments] = useState(null);
    const [pagination, setPagination] = useState({});
    const [metrics, setMetrics] = useState({});
    const [isEmptyData, setIsEmptyData] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const currentPage = parseInt(searchParams.get("page")) || 1;
    const searchTerm = searchParams.get("search") || "";
    const statusFilter = searchParams.get("status") || "";
    const dateRange = searchParams.get("dateRange") || "";

    const [search, setSearch] = useState(searchTerm);
    const [type, setType] = useState(statusFilter);
    const [selectedRange, setSelectedRange] = useState(dateRange);
    const [isPdfGenerating, setIsPdfGenerating] = useState(false);

    const itemsPerPage = 10;

    useEffect(() => {
        fetchPayments();
    }, [searchParams]);

    const fetchPayments = async (download = false, monthRange = 1, page = currentPage, searchValue = search, statusValue = type, rangeValue = selectedRange) => {
        
        let { startDate, endDate } = getDateRangeFromSelection(rangeValue);

        if(download && !startDate && !endDate) {
            // Apply 6-month limit when no date range is selected
            const today = new Date();
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(today.getMonth() - 6);
            startDate = sixMonthsAgo;
            endDate = today;
            monthRange = 7;
        }

        const params = {
            offset: download ? null : (page - 1) * itemsPerPage,
            limit: download ? null : itemsPerPage,
            searchTerm: searchValue,
            status: statusValue,
            startDate: startDate ? startDate : null,
            endDate: endDate ? endDate : null,
            month: startDate ? monthArray[startDate.getMonth()] : null,
            monthRange: monthRange,
        };

        
        const result = await FetachingGetTenantPayments(params);
        const data = result?.response?.data;
        
        if (result?.success && !download) {
            setTenantPayments(data?.data);
            setIsEmptyData(data?.data.length === 0);
            setPagination(data?.pagination || {});
            setMetrics(data?.metrics || {});
        }
        
        return {data, startDate, endDate};
    };

    const handlePageChange = (page) => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("page", page);
            if (search) newParams.set("search", search);
            if (type) newParams.set("status", type);
            if (selectedRange) newParams.set("dateRange", selectedRange);
            return newParams;
        });
    };

    const handleSearch = () => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            if (search) newParams.set("search", search);
            else newParams.delete("search");
            if (type) newParams.set("status", type);
            else newParams.delete("status");
            if (selectedRange) newParams.set("dateRange", selectedRange);
            else newParams.delete("dateRange");
            newParams.set("page", 1);
            return newParams;
        });
    };

    const transformPayments = (data) => {
        return (data || []).map((item, index) => ({
            key: `payout-${index}`,
            id: new Date(item.paymentDate).toLocaleDateString() + " " + new Date(item.paymentDate).toLocaleTimeString(),
            paymentDate: item.paymentDate,
            tenantName: item.tenantName,
            property: item.unitProperty.replace('@', '/'), // Replace "/" with "@" in Unit / Property field
            unitName: item.landlordName,
            paid: `UGX ${item.amount.toLocaleString()}`,
            amountLandloard: item.amount,
            platformCommission: 0, // Not provided in API response
            paymentMethod: item.paymentMethod,
            reference: item.transactionId,
            status: item.transactionStatus === "SUCCESS" ? "Paid" : item.transactionStatus,
            payments: [{
                paymentMethod: item.paymentMethod,
                status: item.transactionStatus === "SUCCESS" ? "Paid" : item.transactionStatus,
                reference: item.transactionId,
                amount: item.amount,
                paymentDate: item.paymentDate
            }],
        }));
    }
    // Transform API data to match table structure
    const transformedPayouts = transformPayments(tenantPayments);

    const columns = [
        { title: "Date / Time", dataIndex: "id", key: "id" },
        { title: "Tenant Name", dataIndex: "tenantName", key: "tenantName" },
        { title: "Unit / Property", dataIndex: "property", key: "property" },
        { title: "Landlord", dataIndex: "unitName", key: "unitName" },
        { title: "Amount Paid", dataIndex: "paid", key: "paid"},
        {
            title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Status</span>,
            dataIndex: "status",
            key: "status",
            render: (status) => (
              <span
                className={`text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] px-2 py-1 rounded font-medium ${
                  status === "Paid"
                    ? "bg-[#DCFCE7] text-[#3fab67]"
                    : status === "PROCESSING"
                        ? "bg-[#E0F4FF] text-[#0078D4]"
                        : status === "FAILED"
                        ? "bg-[#FEE2E2] text-[#EF4444]"
                        : "bg-[#F1F5F9] text-[#9E9E9E]"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
              </span>
            ),
        },
    ];

    const subTableConfig = {
        dataField: "payments",
        columns: [
            {
                title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Payment Method</span>,
                dataIndex: "paymentMethod",
                key: "paymentMethod",
                render: (method) => method === "MOBILE_MONEY" ? "Mobile Money" : method === "BANK" ? "Bank Transfer" : method,
            },
           /* {
                title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Status</span>,
                dataIndex: "status",
                key: "status",
                render: (status) => (
                  <span
                    className={`text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] px-2 py-1 rounded font-medium ${
                      status === "PAID"
                        ? "bg-[#DCFCE7] text-[#3fab67]"
                        : status === "PROCESSING"
                            ? "bg-[#E0F4FF] text-[#0078D4]"
                            : status === "FAILED"
                            ? "bg-[#FEE2E2] text-[#EF4444]"
                            : "bg-[#F1F5F9] text-[#9E9E9E]"
                    }`}
                  >
                    {status}
                  </span>
                ),
            }, */
            {
                title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Reference ID</span>,
                dataIndex: "reference",
                key: "reference",
            },
        ],
    };

    const createInfoCardSVG = (data) => {
        // Calculate complexity scores for each value
        const rentCollectedStr = data.totalRentCollected.toFixed(2);
        const rentExpectedStr = data.totalRentExpected.toFixed(2);
        const propertiesStr = data.totalUniqueProperties.toString();
        const unitsStr = data.totalUniqueUnits.toString();

        // Determine space needs
        const rentCollectedNeedsSpace = rentCollectedStr.length > 12;
        const rentExpectedNeedsSpace = rentExpectedStr.length > 12;
        const bothRentNeedSpace = rentCollectedNeedsSpace || rentExpectedNeedsSpace;

        // Dynamic width allocation strategy
        let widths;
        if (bothRentNeedSpace) {
        // Prioritize rent cards, minimize property/unit cards
        widths = {
            card1: rentCollectedNeedsSpace ? 250 : 220,
            card2: rentExpectedNeedsSpace ? 250 : 220,
            card3: 100, // Minimal for properties
            card4: 100, // Minimal for units
        };
        } else {
        // Balanced layout for normal values
        widths = {
            card1: 200,
            card2: 200,
            card3: 140,
            card4: 140,
        };
        }

        // Helper function to create text with smart wrapping
        const createTextElement = (value, x, y, fontSize, maxWidth) => {
        const valueStr = value.toString();
        const estimatedWidth = valueStr.length * (fontSize * 0.6);
        
        if (estimatedWidth <= maxWidth) {
            return `
            <text x="${x}" y="${y}" font-family="Inter, sans-serif" font-size="${fontSize}" font-weight="bold" fill="url(#textGradient)">
                ${valueStr}
            </text>
            `;
        } else {
            // Split into two lines
            const commaIndex = valueStr.indexOf(',');
            let firstLine, secondLine;
            
            if (commaIndex !== -1 && commaIndex > 2) {
            firstLine = valueStr.substring(0, commaIndex + 1);
            secondLine = valueStr.substring(commaIndex + 1);
            } else {
            const midPoint = Math.floor(valueStr.length / 2);
            firstLine = valueStr.substring(0, midPoint);
            secondLine = valueStr.substring(midPoint);
            }
            
            const adjustedFontSize = fontSize - 2; // Slightly smaller for wrapped text
            return `
            <text x="${x}" y="${y - 8}" font-family="Inter, sans-serif" font-size="${adjustedFontSize}" font-weight="bold" fill="url(#textGradient)">
                ${firstLine}
            </text>
            <text x="${x}" y="${y + 10}" font-family="Inter, sans-serif" font-size="${adjustedFontSize}" font-weight="bold" fill="url(#textGradient)">
                ${secondLine}
            </text>
            `;
        }
        };

        // Calculate positions
        const positions = {
        card1: 20,
        card2: 20 + widths.card1 + 20,
        card3: 20 + widths.card1 + 20 + widths.card2 + 20,
        card4: 20 + widths.card1 + 20 + widths.card2 + 20 + widths.card3 + 20,
        };

        const totalWidth = positions.card4 + widths.card4 + 20;

        const svgContent = `
        <svg width="${totalWidth}" height="200" xmlns="http://www.w3.org/2000/svg">
            <defs>
            <linearGradient id="textGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style="stop-color:#4E6CF0;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#8442E3;stop-opacity:1" />
            </linearGradient>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="2" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.1)"/>
            </filter>
            </defs>
            
            <!-- Card 1: Rent Collected -->
            <rect x="${positions.card1}" y="40" width="${widths.card1}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
            <text x="${positions.card1 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
            Rent Collected (UGX)
            </text>
            ${createTextElement(rentCollectedStr, positions.card1 + 10, 100, 20, widths.card1 - 20)}
            
            <!-- Card 2: Rent Expected -->
            <rect x="${positions.card2}" y="40" width="${widths.card2}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
            <text x="${positions.card2 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
            Rent Expected (UGX)
            </text>
            ${createTextElement(rentExpectedStr, positions.card2 + 10, 100, 20, widths.card2 - 20)}
            
            <!-- Card 3: Properties -->
            <rect x="${positions.card3}" y="40" width="${widths.card3}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
            <text x="${positions.card3 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
            Properties
            </text>
            ${createTextElement(propertiesStr, positions.card3 + 10, 105, 36, widths.card3 - 20)}
            
            <!-- Card 4: Units -->
            <rect x="${positions.card4}" y="40" width="${widths.card4}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
            <text x="${positions.card4 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
            Units
            </text>
            ${createTextElement(unitsStr, positions.card4 + 10, 105, 36, widths.card4 - 20)}
        </svg>
        `;
        
        return svgContent;
    };

    const handleDownloadPDF = async () => {
        try {

        const { data, startDate, endDate } = await fetchPayments(true);

        let filteredDataForDownload = transformPayments(data?.data);

        const startDateFormatted = formatDateToISO(startDate, false).split('.')[0];
        const endDateFormatted = formatDateToISO(endDate, true).split('.')[0];
        let dateRangeText = `${startDateFormatted} to ${endDateFormatted}`;
        let searchTerm = search ? search.trim() : '';
        let status = type ? type.trim() : 'All';

        if (filteredDataForDownload.length === 0) {
            showToast("warning", "No data available for the selected date range.");
            setIsPdfGenerating(false);
            return;
        }
        // Add progress indication for large datasets
        if (filteredDataForDownload.length > 500) {
            showToast("info", "Generating PDF for large dataset. Please wait...");
        }

        applyPlugin(jsPDF);
        const pdf = new jsPDF('p', 'mm', 'a4');

        let pageHeight = pdf.internal.pageSize.height;
        let pageWidth = pdf.internal.pageSize.width;

        let yPosition = 20;
        let xPosition = 20;
        
        // Contact fields 
        pdf.setFontSize(7);
        pdf.setTextColor(60, 60, 60);
        pdf.setFont("helvetica", "normal");
        
        pdf.text('Address:', xPosition, yPosition);
        pdf.text('TENAFIX LIMITED, P.O BOX 191810 KAMPALA UGANDA, CENTRAL, KAMPALA, KAWEMPE DIVISION, MULAGO 1, UPPER MAWANDA', xPosition + 15, yPosition + 0);
  
        pdf.text('Contact:', xPosition, yPosition + 5);
        pdf.text('+256 773 884476, +256 776 003035', xPosition + 15, yPosition + 5);
  
        pdf.text('Email:', xPosition, yPosition + 10);
        pdf.text('support@tenafix.co', xPosition + 15, yPosition + 10);

        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.2);
        pdf.line(xPosition, yPosition + 12, pageWidth - 20, yPosition + 12);

        yPosition += 20;

        pdf.setFontSize(16);
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenant Payment Report', 20, yPosition);

        yPosition += 10;

        // Report metadata
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        pdf.setFont("helvetica", "normal");
        pdf.text('Reporting Period:', 20, yPosition);

        pdf.setTextColor(0, 0, 0);
        pdf.text(dateRangeText, 50, yPosition);
        yPosition += 7;

        pdf.setTextColor(60, 60, 60);
        pdf.text('Generated on:', 20, yPosition);
        pdf.setTextColor(0, 0, 0);
        const now = new Date();
        const generationTime = `${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;
        pdf.text(generationTime, 50, yPosition);
        yPosition += 15;

        // Applied filters section
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "bold");
        pdf.text('Applied Filters', 20, yPosition);
        yPosition += 8;

        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
        pdf.setFont("helvetica", "normal");
        
        if (searchTerm) {
            pdf.text(`• Search Filter: ${searchTerm}`, 25, yPosition);
            yPosition += 5;
        }

        if (status) {
            pdf.text(`• Status Filter: ${status}`, 25, yPosition);
            yPosition += 5;
        }
        
        pdf.text(`• Date Range: ${dateRangeText}`, 25, yPosition);
        yPosition += 10;

        // Create SVG InfoCard with gradients
        const svgContent = createInfoCardSVG(data?.metrics);

        try {
            const infoCardCanvas = await svgToCanvas(svgContent, 800, 200);
            if (infoCardCanvas) {
                const infoCardImgData = infoCardCanvas.toDataURL('image/png');
                const infoCardWidth = 170; // Full width within margins
                const infoCardHeight = (200 * infoCardWidth) / 800; // Proportional height calculation

                pdf.addImage(infoCardImgData, 'PNG', 20, yPosition, infoCardWidth, infoCardHeight);
                yPosition += infoCardHeight - 10;
            }
        } catch (svgError) {
            showToast("error", "Failed to generate info card image. Please try again.");
        }

        const autoTableData = filteredDataForDownload.map(item => [
            item.id, // Date / Time
            item.tenantName, // Tenant Name  
            item.property, // Unit / Property
            item.unitName, // Landlord
            item.paid, // Amount Paid
            item.payments[0]?.paymentMethod === "MOBILE_MONEY" ? "Mobile Money" : 
            item.payments[0]?.paymentMethod === "BANK" ? "Bank Transfer" : 
            item.payments[0]?.paymentMethod || "N/A", // Payment Method
            item.payments[0]?.status || "N/A", // Status
            item.payments[0]?.reference || "N/A" // Reference ID
        ]);

        const tableHeaders = [
            'Date / Time', 
            'Tenant Name', 
            'Unit / Property', 
            'Landlord', 
            'Amount Paid',
            'Payment Method',
            'Status', 
            'Reference ID'
        ];

        pdf.autoTable({
            head: [tableHeaders],
            body: autoTableData,
            startY: yPosition + 10,
            theme: 'grid',
            headStyles: {
            fillColor: [71, 114, 241],
            textColor: [255, 255, 255],
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'center'
            },
            bodyStyles: {
            fontSize: 7,
            textColor: [0, 0, 0],
            cellPadding: 2
            },
            alternateRowStyles: {
            fillColor: [248, 249, 250] // Light gray for alternate rows
            },
            columnStyles: {
                0: { halign: 'center', cellWidth: 20 },     // Date / Time
                1: { halign: 'left', cellWidth: 20 },       // Tenant Name
                2: { halign: 'center', cellWidth: 25 },     // Unit / Property
                3: { halign: 'left', cellWidth: 20 },       // Landlord
                4: { halign: 'right', cellWidth: 20 },      // Amount Paid
                5: { halign: 'center', cellWidth: 20 },     // Payment Method
                6: { halign: 'center', cellWidth: 20 },     // Status
                7: { halign: 'center', cellWidth: 25 },     // Reference ID
            },
            margin: { left: 20, right: 20, top: 20, bottom: 20 },
            styles: {
            overflow: 'linebreak',
            fontSize: 9
            }
        });

        // Add header and footer to all pages
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            
            const footerY = pageHeight - 15;

            // header section
            pdf.setFontSize(10);
            pdf.setTextColor(71, 114, 241); // Blue color for brand
            pdf.setFont("helvetica", "bold");
            pdf.text('Tenafix Limited', 20, 10);
            
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            pdf.setFont("helvetica", "normal");
            pdf.text('Real Estate Management Software', 20, 14);
            
            pdf.addImage(Logo, 'PNG', pageWidth - 40, -5, 30, 30);
            
            // Footer section
            pdf.setFontSize(8);
            pdf.setTextColor(71, 114, 241);
            pdf.setFont("helvetica", "bold");
            pdf.text('Tenafix Limited', 20, footerY + 3);
            
            pdf.setTextColor(100, 100, 100);
            pdf.setFont("helvetica", "normal");
            pdf.text('|', 42, footerY + 3);
            pdf.text('www.tenafix.co', 44, footerY + 3);
            pdf.text('|', 64, footerY + 3);
            pdf.text('support@tenafix.co', 66, footerY + 3);
            
            // System generated note
            pdf.setFontSize(7);
            pdf.setTextColor(120, 120, 120);
            pdf.text('This is a system-generated report.', 20, footerY + 6);
            pdf.text('For assistance contact support@tenafix.co', 20, footerY + 9);
            
            // Page number (right aligned)
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            const pageText = `Page ${i} of ${pageCount}`;
            const pageTextWidth = pdf.getTextWidth(pageText);
            pdf.text(pageText, pdf.internal.pageSize.width - pageTextWidth - 20, footerY + 8);
        }

        // Save PDF
        const localNow = new Date().toLocaleString().split(', ');
        const currentDate = `${localNow[0]}/${localNow[1].split(' ')[0]}`.replace(/[/:]/g, '-');
        pdf.save(`tenant-payment-report-${currentDate}.pdf`);
        
        } catch (error) {
        showToast("error", "Failed to generate PDF. Please try again.");
        } finally {
        setIsPdfGenerating(false);
        }
    };

    const handleDownloadClick = () => {
        setIsPdfGenerating(true);
        setTimeout(() => handleDownloadPDF(), 0);
    };

    return (
        <div className="min-h-screen p-4 sm:p-6 md:p-[30px]">
            <TableHeader
                searchValue={search}
                onSearchChange={setSearch}
                onSearchSubmit={handleSearch}
                searchPlaceholder="Search by tenant name, landlord, or unit ID"
                filters={[
                    {
                        name: "Status",
                        value: type,
                        onChange: setType,
                        options: [
                            { label: "All", value: "" },
                            { label: "Success", value: "SUCCESS" },
                            { label: "Processing", value: "PROCESSING" },
                            { label: "Failed", value: "FAILED" },
                        ],
                    },
                ]}
                dateRangeFilter={{
                    value: selectedRange,
                    onChange: setSelectedRange,
                    options: [
                        { label: "All Time", value: "" },
                        { label: "Today", value: "Today" },
                        { label: "This Week", value: "This Week" },
                        { label: "This Month", value: "This Month" },
                        { label: "Last Month", value: "Last Month" },
                    ],
                }}
                buttons={[
                    {
                        label: "Search",
                        onClick: handleSearch,
                        className: "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] uppercase w-[100px] ",
                    },
                    {
                        label: "DOWNLOAD REPORT",
                        onClick: handleDownloadClick,
                        loading: isPdfGenerating || isFetchingGetTenantPayments,
                        disabled: isPdfGenerating || isFetchingGetTenantPayments || isEmptyData,
                        variant: "primary",
                        className:
                            "bg-[#C084FC] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-full max-w-[170px] uppercase font-bold",
                    }
                ]}
                containerClass="bg-white  flex-wrap md:grid md:grid-cols-2 lg:grid lg:grid-cols-2 md:gap-y-[30px] [@media(min-width:1120px)]:grid [@media(min-width:1120px)]:grid-cols-3 [@media(min-width:1516px)]:grid [@media(min-width:1516px)]:grid-cols-4 justify-between  p-3 sm:p-4 md:p-6 lg:p-[30px] "
                filterDropdownClass="w-full md:w-full lg:w-full [@media(min-width:1516px)]:w-[247px] [@media(min-width:1516px)]:w-full"
                searchInputClass="text-gray-700 h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] md:w-full [@media(min-width:1516px)]:w-[247px] [@media(min-width:1516px)]:w-full"
                datePickerClass="gap-2"
                searchTooltip="Search by tenant name, landlord, or unit ID"
                datePickerInputClass="w-full sm:w-full md:w-full lg:w-full [@media(min-width:1516px)]:w-full [@media(min-width:1708px)]:w-[247px] border"
                buttonWrapperClass="justify-center sm:justify-start  md:gap-[10px] lg:gap-[10px] [@media(min-width:1516px)]:gap-[10px] [@media(min-width:1300px)]:ml-[380px] [@media(min-width:1120px)]:ml-[280px]  [@media(min-width:1516px)]:ml-[0px]  [@media(min-width:1516px)]:justify-end"
                buttonClass="uppercase tracking-wide flex justify-center"
            />

            <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
                {/* Stat Cards */}
                <div className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px] $ grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                        <InfoCard
                            label="Rent Collected (UGX)"
                            value={metrics.totalRentCollected?.toLocaleString() || "0"}
                            className=" max-h-[100px] mx-auto"
                            labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
                            valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            popoverTextStyle="text-level-4"
                        />
                    </div>
                    <div>
                        <InfoCard
                            label="Rent Expected (UGX)"
                            value={Math.round(metrics.totalRentExpected || 0).toLocaleString()}
                            className=" max-h-[100px] mx-auto"
                            labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
                            valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            popoverTextStyle="text-level-4"
                        />
                    </div>
                    <div>
                        <InfoCard
                            label="Properties"
                            value={metrics.totalUniqueProperties?.toString().padStart(2, '0') || "00"}
                            className=" max-h-[100px] mx-auto"
                            labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            popoverTextStyle="text-level-4"
                        />
                    </div>
                    <div>
                        <InfoCard
                            label="Units"
                            value={metrics.totalUniqueUnits?.toString().padStart(2, '0') || "00"}
                            className=" max-h-[100px] mx-auto"
                            labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            popoverTextStyle="text-level-4"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[8px] border border-[#E5E5E5]">
                {isFetchingGetTenantPayments && !isPdfGenerating? (
                    <Loader color="#4772F1" />
                ) : isEmptyData ? (
                    <NotFound
                        text1="No payments found"
                        text2="No payment data matches your current filter criteria."
                        isButton={false}
                    />
                ) : errorFetachingGetTenantPayments ? (
                    <NotFound
                        text1="Error fetching payments"
                        text2={errorFetachingGetTenantPayments?.message || "An error occurred while fetching payment data."}
                        isButton={false}
                    />
                ) : (
                    <LandLordTable
                        dataSource={transformedPayouts}
                        columns={columns}
                        hasSubTable={true}
                        subTableConfig={subTableConfig}
                        rowKey="key"
                        headingTextColor="#4772F1"
                    />
                )}
            </div>

            {/* Pagination */}
            {!isEmptyData &&  (
                <div className="flex justify-center mt-6">
                    <Pagination
                        current={currentPage || 1}
                        total={pagination?.totalDocuments || 0}
                        pageSize={itemsPerPage}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        hideOnSinglePage={false}
                        showLessItems
                        className="custom-pagination"
                    />
                </div>
            )}
        </div>
    );
};

export default TenantPaymentActivity;
